import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Breadcrums from "../../common/breadcrumbs";
import CustomPagination from "../../common/custompagination";
import Loader from "../../common/loader";
import { formateDateWithMonth } from "../../../../utils/commonfunction";
import * as broadcastService from "../../services/broadcast.service";
import Swal from "sweetalert2";
import { SWAL_SETTINGS, ROLE } from "../../../../utils/Constants";
import { Modal, Button } from "rsuite";

const ROLE_DISPLAY_CONFIG = {
  [ROLE.CLUB_ADMIN]: { label: "Club Admin", bg: "#e0f2fe", color: "#0369a1", border: "#bae6fd" },
  [ROLE.TEAM_MANAGER]: { label: "Team Manager", bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  [ROLE.PARENT]: { label: "Parent", bg: "#faf5ff", color: "#7e22ce", border: "#f3e8ff" },
  [ROLE.PLAYER]: { label: "Player", bg: "#fef3c7", color: "#b45309", border: "#fde68a" },
  [ROLE.NON_AFFILIATED_USER]: { label: "Basic User", bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" },
  [ROLE.SUB_ADMIN]: { label: "Sub Admin", bg: "#fff1f2", color: "#be123c", border: "#fecdd3" },
};

const BroadcastPage = () => {
  const { t } = useTranslation();

  // Navigation Tabs
  const [activeTab, setActiveTab] = useState("send"); // "send" | "history"

  // Form State
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Audience Targeting State
  const [targetType, setTargetType] = useState("ALL"); // "ALL" | "ROLES" | "SELECTED"
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // User List & Filter State (for Specific User selection)
  const [usersList, setUsersList] = useState([]);
  const [totalEligible, setTotalEligible] = useState(0);
  const [totalFilteredUsers, setTotalFilteredUsers] = useState(0);
  const [roleCounts, setRoleCounts] = useState({});
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("ALL");
  const [userPage, setUserPage] = useState(1);
  const [userLimit] = useState(10);

  // Broadcast History State
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit] = useState(10);
  const [historySearch, setHistorySearch] = useState("");

  // Details Modal State
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard") || "Dashboard", url: "/admin/dashboard" },
    { title: "Broadcast", url: "" },
  ];

  // Fetch Users for Targeting
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await broadcastService.GetBroadcastUsers({
        role: userRoleFilter,
        search: userSearch,
        page: userPage,
        limit: userLimit,
      });

      if (res && res.success) {
        setUsersList(res.data?.list || []);
        setTotalFilteredUsers(res.data?.total_records || 0);
        setTotalEligible(res.data?.total_eligible || 0);
        setRoleCounts(res.data?.role_counts || {});
      }
    } catch (err) {
      console.error("Failed to load broadcast users:", err);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "send") {
      fetchUsers();
    }
  }, [userPage, userRoleFilter, activeTab]);

  // Debounced search for user selection table
  useEffect(() => {
    if (activeTab !== "send") return;
    const timer = setTimeout(() => {
      setUserPage(1);
      fetchUsers();
    }, 400);
    return () => clearTimeout(timer);
  }, [userSearch]);

  // Fetch Broadcast History
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await broadcastService.GetBroadcastHistory({
        page: historyPage,
        limit: historyLimit,
        search: historySearch,
      });

      if (res && res.success) {
        setHistoryList(res.data?.list || []);
        setHistoryTotal(res.data?.total_records || 0);
      }
    } catch (err) {
      console.error("Failed to load broadcast history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    }
  }, [activeTab, historyPage]);

  // Debounced search for history table
  useEffect(() => {
    if (activeTab !== "history") return;
    const timer = setTimeout(() => {
      setHistoryPage(1);
      fetchHistory();
    }, 400);
    return () => clearTimeout(timer);
  }, [historySearch]);

  // Compute calculated recipient count based on mode
  const calculatedRecipientCount = useMemo(() => {
    if (targetType === "ALL") {
      return totalEligible;
    }
    if (targetType === "ROLES") {
      return selectedRoles.reduce((sum, r) => sum + (roleCounts[r] || 0), 0);
    }
    if (targetType === "SELECTED") {
      return selectedUserIds.length;
    }
    return 0;
  }, [targetType, totalEligible, selectedRoles, roleCounts, selectedUserIds]);

  // Toggle role selection
  const handleToggleRole = (roleKey) => {
    setSelectedRoles((prev) =>
      prev.includes(roleKey) ? prev.filter((r) => r !== roleKey) : [...prev, roleKey]
    );
  };

  // Toggle individual user checkbox
  const handleToggleUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  // Select / Deselect all users on current page
  const isAllCurrentPageSelected =
    usersList.length > 0 && usersList.every((u) => selectedUserIds.includes(u.id));

  const handleToggleSelectCurrentPage = () => {
    if (isAllCurrentPageSelected) {
      const pageIds = new Set(usersList.map((u) => u.id));
      setSelectedUserIds((prev) => prev.filter((id) => !pageIds.has(id)));
    } else {
      const newIds = new Set([...selectedUserIds, ...usersList.map((u) => u.id)]);
      setSelectedUserIds(Array.from(newIds));
    }
  };

  // Select ALL filtered users across all pages
  const handleSelectAllFilteredUsers = async () => {
    try {
      setUsersLoading(true);
      const res = await broadcastService.GetBroadcastUsers({
        role: userRoleFilter,
        search: userSearch,
        all: true,
      });

      if (res && res.success && res.data?.list) {
        const allIds = res.data.list.map((u) => u.id);
        setSelectedUserIds(Array.from(new Set([...selectedUserIds, ...allIds])));
        Swal.fire({
          icon: "success",
          title: "All Filtered Users Selected",
          text: `Added ${allIds.length} user(s) to your selection.`,
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUsersLoading(false);
    }
  };

  // Send Broadcast Handler
  const handleSendBroadcast = () => {
    if (!subject.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Subject Required",
        text: "Please enter an announcement subject.",
        ...SWAL_SETTINGS,
      });
      return;
    }

    if (!message.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Message Required",
        text: "Please enter your announcement message content.",
        ...SWAL_SETTINGS,
      });
      return;
    }

    if (calculatedRecipientCount === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Recipients Selected",
        text: "Please select an audience or choose specific users to send to.",
        ...SWAL_SETTINGS,
      });
      return;
    }

    let targetDesc = "all users";
    if (targetType === "ROLES") {
      targetDesc = `users with roles (${selectedRoles.join(", ")})`;
    } else if (targetType === "SELECTED") {
      targetDesc = `${selectedUserIds.length} specifically selected users`;
    }

    Swal.fire({
      title: "Send Broadcast Email?",
      html: `
        <div style="text-align: left; font-size: 14px;">
          <p><strong>Subject:</strong> ${subject}</p>
          <p><strong>Target Audience:</strong> ${targetDesc}</p>
          <p><strong>Total Recipients:</strong> <span style="color: #097969; font-weight: bold; font-size: 16px;">${calculatedRecipientCount} user(s)</span></p>
          <hr/>
          <p style="color: #64748b; font-size: 13px;">
            ⚠️ This will send an official announcement email to the recipients. Recipients cannot see each other's email addresses.
          </p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#097969",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Send Broadcast Now",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsSending(true);
        try {
          const payload = {
            subject: subject.trim(),
            message: message.trim(),
            targetType,
            targetRoles: targetType === "ROLES" ? selectedRoles : [],
            selectedUserIds: targetType === "SELECTED" ? selectedUserIds : [],
            sendToAll: targetType === "ALL",
          };

          const res = await broadcastService.SendBroadcast(payload);

          if (res && res.success) {
            Swal.fire({
              icon: "success",
              title: "Broadcast Sent!",
              text: `Your announcement has been scheduled to deliver to ${calculatedRecipientCount} user(s).`,
              confirmButtonColor: "#097969",
            });
            // Reset form
            setSubject("");
            setMessage("");
            setSelectedUserIds([]);
            setSelectedRoles([]);
            setTargetType("ALL");
            // Switch to History
            setActiveTab("history");
          } else {
            Swal.fire({
              icon: "error",
              title: "Failed to Send",
              text: res?.message || "An error occurred while sending the broadcast.",
            });
          }
        } catch (err) {
          console.error("Broadcast send error:", err);
          Swal.fire({
            icon: "error",
            title: "Transmission Error",
            text: err?.response?.data?.message || err.message || "Failed to communicate with the server.",
          });
        } finally {
          setIsSending(false);
        }
      }
    });
  };

  return (
    <div className="main-content-body">
      <Breadcrums breadcrumbs={breadcrumbs} />

      {/* Main Container Card */}
      <div className="card custom-card">
        <div className="card-body">
          {/* Header & Tabs */}
          <div className="d-flex flex-wrap justify-content-between align-items-center pb-3 mb-4 border-bottom">
            <div>
              <h4 className="main-content-label mb-1 d-flex align-items-center gap-2">
                <i className="fa fa-bullhorn text-primary" style={{ fontSize: "1.25rem" }}></i>
                Broadcast Announcements
              </h4>
              <p className="text-muted tx-13 mb-0">
                Dispatch platform announcements, update alerts, or maintenance notifications to users across the app.
              </p>
            </div>

            {/* Top Navigation Tabs */}
            <ul className="nav nav-pills mt-2 mt-sm-0" style={{ gap: "8px" }}>
              <li className="nav-item">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    activeTab === "send" ? "btn-primary font-weight-bold" : "btn-outline-primary"
                  }`}
                  onClick={() => setActiveTab("send")}
                  style={{ borderRadius: "6px", padding: "7px 18px" }}
                >
                  <i className="fe fe-send me-1"></i> Send Broadcast
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    activeTab === "history" ? "btn-primary font-weight-bold" : "btn-outline-primary"
                  }`}
                  onClick={() => setActiveTab("history")}
                  style={{ borderRadius: "6px", padding: "7px 18px" }}
                >
                  <i className="fe fe-clock me-1"></i> Broadcast History
                </button>
              </li>
            </ul>
          </div>

          {/* TAB 1: SEND BROADCAST */}
          {activeTab === "send" && (
            <div className="row">
              {/* Left Column: Message Composer */}
              <div className="col-lg-6 col-md-12 mb-4">
                <div
                  className="p-4"
                  style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="font-weight-bold mb-0 text-dark">
                      <i className="fe fe-edit-3 me-1 text-primary"></i> 1. Compose Announcement
                    </h6>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-secondary"
                      onClick={() => setPreviewMode(!previewMode)}
                    >
                      <i className={`fe ${previewMode ? "fe-edit" : "fe-eye"} me-1`}></i>
                      {previewMode ? "Edit Mode" : "Preview Email"}
                    </button>
                  </div>

                  {/* Subject Input */}
                  <div className="form-group mb-3">
                    <label className="font-weight-semibold tx-13 text-dark">
                      Announcement Subject <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Important Platform Update: New Features Released"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      maxLength={150}
                      disabled={previewMode}
                      style={{ fontSize: "14px", borderRadius: "6px" }}
                    />
                    <div className="d-flex justify-content-between text-muted tx-11 mt-1">
                      <span>Make it concise and descriptive</span>
                      <span>{subject.length}/150</span>
                    </div>
                  </div>

                  {/* Message Content */}
                  <div className="form-group mb-3">
                    <label className="font-weight-semibold tx-13 text-dark">
                      Message Body <span className="text-danger">*</span>
                    </label>

                    {previewMode ? (
                      <div
                        className="p-3 bg-white border rounded"
                        style={{
                          minHeight: "260px",
                          maxHeight: "450px",
                          overflowY: "auto",
                          borderRadius: "8px",
                          fontSize: "14px",
                          lineHeight: "1.6",
                          backgroundColor: "#f4f7f8",
                        }}
                      >
                        <div
                          style={{
                            maxWidth: "540px",
                            margin: "0 auto",
                            backgroundColor: "#ffffff",
                            borderRadius: "8px",
                            overflow: "hidden",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                          }}
                        >
                          {/* Official Header */}
                          <div
                            style={{
                              padding: "20px 24px",
                              textAlign: "center",
                              borderBottom: "2px solid #1f8a8a",
                              backgroundColor: "#ffffff",
                            }}
                          >
                            <h4 style={{ margin: 0, color: "#0f2963", fontWeight: 800, letterSpacing: "0.5px" }}>
                              mNetrics
                            </h4>
                            <p style={{ margin: "2px 0 0 0", color: "#1f8a8a", fontSize: "11px", fontWeight: "bold" }}>
                              Mobile &bull; Match &bull; Metrics
                            </p>
                          </div>

                          {/* Email Body */}
                          <div style={{ padding: "24px", color: "#333333", fontSize: "14px", lineHeight: "1.6" }}>
                            <h5 style={{ color: "#0f2963", fontWeight: 700, marginBottom: "16px" }}>
                              {subject || "Announcement Subject"}
                            </h5>

                            <div
                              style={{ whiteSpace: "pre-line" }}
                              dangerouslySetInnerHTML={{
                                __html: message
                                  ? message.replace(/\n/g, "<br/>")
                                  : "<em class='text-muted'>No message content entered yet...</em>",
                              }}
                            />

                            {/* Need Help Section */}
                            <div style={{ marginTop: "24px", borderTop: "1px dashed #e2e8f0", paddingTop: "16px" }}>
                              <p style={{ margin: 0, fontSize: "13px", fontWeight: "bold", color: "#0f2963" }}>
                                Need help getting started?
                              </p>
                              <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#64748b" }}>
                                <span style={{ color: "#1f8a8a", fontWeight: "bold", textDecoration: "underline" }}>Help Centre</span> &mdash; answers to common questions and support guides.
                              </p>
                            </div>

                            {/* Signature */}
                            <div style={{ marginTop: "20px", borderTop: "1px solid #f1f5f9", paddingTop: "14px" }}>
                              <p style={{ margin: 0, fontSize: "12px", color: "#475569" }}>
                                Warm regards,<br />
                                <strong style={{ color: "#0f2963" }}>The mNetrics Team</strong><br />
                                <span style={{ color: "#1f8a8a" }}>www.mnetrics.app</span>
                              </p>
                            </div>
                          </div>

                          {/* Footer */}
                          <div style={{ backgroundColor: "#f8fafc", padding: "14px", textAlign: "center", borderTop: "1px solid #e2e8f0", fontSize: "11px", color: "#94a3b8" }}>
                            <p style={{ margin: 0, color: "#1f8a8a", fontWeight: "bold" }}>www.mnetrics.app</p>
                            <p style={{ margin: "2px 0 0 0" }}>Mobile &bull; Match &bull; Metrics &bull; &copy; {new Date().getFullYear()} mNetrics.</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <textarea
                          className="form-control"
                          rows="10"
                          placeholder="Type your announcement message here...&#10;&#10;Dear Users,&#10;&#10;We are excited to share that a new update is now live on the mNetrics mobile and web apps..."
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          style={{
                            borderRadius: "6px",
                            fontSize: "14px",
                            lineHeight: "1.6",
                            resize: "vertical",
                          }}
                        ></textarea>
                        <div className="d-flex justify-content-between text-muted tx-11 mt-1">
                          <span>Standard line breaks and paragraphs will be preserved</span>
                          <span>{message.length} characters</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column: Audience Targeting */}
              <div className="col-lg-6 col-md-12 mb-4">
                <div
                  className="p-4"
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                  }}
                >
                  <h6 className="font-weight-bold mb-3 text-dark">
                    <i className="fe fe-users me-1 text-primary"></i> 2. Target Audience
                  </h6>

                  {/* Mode Selector Cards */}
                  <div className="row g-2 mb-3">
                    {/* Option 1: All Users */}
                    <div className="col-md-4 col-sm-12">
                      <div
                        onClick={() => setTargetType("ALL")}
                        style={{
                          cursor: "pointer",
                          borderRadius: "8px",
                          padding: "14px 12px",
                          border: `2px solid ${targetType === "ALL" ? "#097969" : "#e2e8f0"}`,
                          backgroundColor: targetType === "ALL" ? "#ecfdf5" : "#ffffff",
                          transition: "all 0.2s ease",
                          height: "100%",
                        }}
                      >
                        <div className="d-flex align-items-center mb-1">
                          <input
                            type="radio"
                            name="targetType"
                            checked={targetType === "ALL"}
                            onChange={() => setTargetType("ALL")}
                            className="me-2"
                            style={{ accentColor: "#097969" }}
                          />
                          <span className="font-weight-bold tx-13 text-dark">All Users</span>
                        </div>
                        <p className="text-muted tx-11 mb-2">Send to all users in database</p>
                        <span className="badge bg-primary text-white tx-11">
                          {totalEligible} users
                        </span>
                      </div>
                    </div>

                    {/* Option 2: Filter by Roles */}
                    <div className="col-md-4 col-sm-12">
                      <div
                        onClick={() => setTargetType("ROLES")}
                        style={{
                          cursor: "pointer",
                          borderRadius: "8px",
                          padding: "14px 12px",
                          border: `2px solid ${targetType === "ROLES" ? "#097969" : "#e2e8f0"}`,
                          backgroundColor: targetType === "ROLES" ? "#ecfdf5" : "#ffffff",
                          transition: "all 0.2s ease",
                          height: "100%",
                        }}
                      >
                        <div className="d-flex align-items-center mb-1">
                          <input
                            type="radio"
                            name="targetType"
                            checked={targetType === "ROLES"}
                            onChange={() => setTargetType("ROLES")}
                            className="me-2"
                            style={{ accentColor: "#097969" }}
                          />
                          <span className="font-weight-bold tx-13 text-dark">By Role</span>
                        </div>
                        <p className="text-muted tx-11 mb-2">Filter by user roles</p>
                        <span className="badge bg-info text-white tx-11">
                          {selectedRoles.length} role(s)
                        </span>
                      </div>
                    </div>

                    {/* Option 3: Select Specific Users */}
                    <div className="col-md-4 col-sm-12">
                      <div
                        onClick={() => setTargetType("SELECTED")}
                        style={{
                          cursor: "pointer",
                          borderRadius: "8px",
                          padding: "14px 12px",
                          border: `2px solid ${targetType === "SELECTED" ? "#097969" : "#e2e8f0"}`,
                          backgroundColor: targetType === "SELECTED" ? "#ecfdf5" : "#ffffff",
                          transition: "all 0.2s ease",
                          height: "100%",
                        }}
                      >
                        <div className="d-flex align-items-center mb-1">
                          <input
                            type="radio"
                            name="targetType"
                            checked={targetType === "SELECTED"}
                            onChange={() => setTargetType("SELECTED")}
                            className="me-2"
                            style={{ accentColor: "#097969" }}
                          />
                          <span className="font-weight-bold tx-13 text-dark">Pick Users</span>
                        </div>
                        <p className="text-muted tx-11 mb-2">Select individual users</p>
                        <span className="badge bg-secondary text-white tx-11">
                          {selectedUserIds.length} chosen
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ROLE SELECTION CHECKBOXES (When targetType === "ROLES") */}
                  {targetType === "ROLES" && (
                    <div
                      className="p-3 mb-3"
                      style={{
                        backgroundColor: "#f8fafc",
                        borderRadius: "8px",
                        border: "1px dashed #cbd5e1",
                      }}
                    >
                      <label className="font-weight-semibold tx-12 text-dark mb-2 d-block">
                        Choose Roles to Broadcast to:
                      </label>
                      <div className="d-flex flex-wrap gap-2">
                        {Object.entries(ROLE_DISPLAY_CONFIG).map(([roleKey, conf]) => {
                          const count = roleCounts[roleKey] || 0;
                          const isChecked = selectedRoles.includes(roleKey);
                          return (
                            <button
                              key={roleKey}
                              type="button"
                              onClick={() => handleToggleRole(roleKey)}
                              className="btn btn-sm d-flex align-items-center gap-1"
                              style={{
                                borderRadius: "20px",
                                backgroundColor: isChecked ? conf.bg : "#ffffff",
                                color: isChecked ? conf.color : "#475569",
                                border: `1.5px solid ${isChecked ? conf.color : "#cbd5e1"}`,
                                fontWeight: isChecked ? 600 : 400,
                                padding: "4px 12px",
                                fontSize: "12px",
                              }}
                            >
                              <i
                                className={`fe ${isChecked ? "fe-check-circle" : "fe-circle"}`}
                                style={{ fontSize: "11px" }}
                              ></i>
                              {conf.label} ({count})
                            </button>
                          );
                        })}
                      </div>
                      {selectedRoles.length === 0 && (
                        <small className="text-danger tx-11 mt-2 d-block">
                          * Please click on one or more roles above.
                        </small>
                      )}
                    </div>
                  )}

                  {/* SUMMARY BANNER */}
                  <div
                    className="p-3 mb-3 d-flex align-items-center justify-content-between"
                    style={{
                      borderRadius: "8px",
                      backgroundColor: calculatedRecipientCount > 0 ? "#ecfdf5" : "#fef2f2",
                      border: `1px solid ${calculatedRecipientCount > 0 ? "#a7f3d0" : "#fecaca"}`,
                    }}
                  >
                    <div>
                      <span
                        className="font-weight-bold tx-13"
                        style={{ color: calculatedRecipientCount > 0 ? "#065f46" : "#991b1b" }}
                      >
                        Recipient Delivery Estimate
                      </span>
                      <p className="tx-11 mb-0 text-muted">
                        Excludes super_admin accounts and unverified/empty emails.
                      </p>
                    </div>
                    <div className="text-end">
                      <span
                        className="font-weight-bold"
                        style={{
                          fontSize: "1.25rem",
                          color: calculatedRecipientCount > 0 ? "#097969" : "#dc2626",
                        }}
                      >
                        {calculatedRecipientCount}
                      </span>
                      <small className="d-block tx-11 text-muted">recipients</small>
                    </div>
                  </div>

                  {/* SEND BUTTON */}
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setSubject("");
                        setMessage("");
                        setSelectedUserIds([]);
                        setSelectedRoles([]);
                        setTargetType("ALL");
                      }}
                      disabled={isSending}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                      onClick={handleSendBroadcast}
                      disabled={isSending || calculatedRecipientCount === 0 || !subject.trim() || !message.trim()}
                      style={{
                        padding: "8px 24px",
                        fontSize: "13px",
                        fontWeight: 600,
                        backgroundColor: "#097969",
                        borderColor: "#097969",
                      }}
                    >
                      {isSending ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          Sending Broadcast...
                        </>
                      ) : (
                        <>
                          <i className="fe fe-send"></i>
                          Send Broadcast ({calculatedRecipientCount})
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* USER SELECTION TABLE (Rendered when targetType === "SELECTED" or for browsing users) */}
          {activeTab === "send" && targetType === "SELECTED" && (
            <div className="mt-2 pt-3 border-top pb-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="font-weight-bold mb-1 text-dark">
                    Select Specific Users ({selectedUserIds.length} currently selected)
                  </h6>
                  <p className="text-muted tx-12 mb-0">
                    Use filters or search to find users, then check their boxes to include them in the broadcast.
                  </p>
                </div>

                <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
                  {selectedUserIds.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-xs"
                      onClick={() => setSelectedUserIds([])}
                    >
                      <i className="fe fe-x me-1"></i> Clear Selection ({selectedUserIds.length})
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-xs"
                    onClick={handleSelectAllFilteredUsers}
                    title="Select all users matching current search/filter across all pages"
                  >
                    <i className="fe fe-check-square me-1"></i> Select All Filtered ({totalFilteredUsers})
                  </button>
                </div>
              </div>

              {/* Filter and Search Bar */}
              <div className="row mb-3 g-2">
                <div className="col-md-8 col-sm-12">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="fe fe-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search users by name or email address..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      style={{ fontSize: "13px" }}
                    />
                    {userSearch && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setUserSearch("")}
                      >
                        <i className="fe fe-x"></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="col-md-4 col-sm-12">
                  <select
                    className="form-select"
                    value={userRoleFilter}
                    onChange={(e) => {
                      setUserRoleFilter(e.target.value);
                      setUserPage(1);
                    }}
                    style={{ fontSize: "13px" }}
                  >
                    <option value="ALL">All Roles ({totalEligible})</option>
                    {Object.entries(ROLE_DISPLAY_CONFIG).map(([roleKey, conf]) => (
                      <option key={roleKey} value={roleKey}>
                        {conf.label} ({roleCounts[roleKey] || 0})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Users Table */}
              <div className="table-responsive border rounded">
                <table className="table table-hover table-striped mb-0 text-nowrap" style={{ fontSize: "13px" }}>
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: "40px" }} className="text-center">
                        <input
                          type="checkbox"
                          checked={isAllCurrentPageSelected}
                          onChange={handleToggleSelectCurrentPage}
                          style={{ cursor: "pointer", accentColor: "#097969" }}
                          title="Select all on this page"
                        />
                      </th>
                      <th>SR. NO.</th>
                      <th>USER NAME</th>
                      <th>EMAIL</th>
                      <th>ROLE</th>
                      <th>STATUS</th>
                      <th>JOINED DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <Loader size="sm" content="Loading users..." vertical />
                        </td>
                      </tr>
                    ) : usersList.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          No users found matching the filter criteria.
                        </td>
                      </tr>
                    ) : (
                      usersList.map((user, idx) => {
                        const isChecked = selectedUserIds.includes(user.id);
                        const roleConf = ROLE_DISPLAY_CONFIG[user.role] || {
                          label: user.role,
                          bg: "#f1f5f9",
                          color: "#475569",
                          border: "#cbd5e1",
                        };
                        const serial = (userPage - 1) * userLimit + (idx + 1);

                        return (
                          <tr
                            key={user.id}
                            onClick={() => handleToggleUser(user.id)}
                            style={{
                              cursor: "pointer",
                              backgroundColor: isChecked ? "#f0fdf4" : undefined,
                            }}
                          >
                            <td
                              className="text-center"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleUser(user.id);
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleToggleUser(user.id)}
                                style={{ cursor: "pointer", accentColor: "#097969" }}
                              />
                            </td>
                            <td>{serial}</td>
                            <td>
                              <span className="font-weight-semibold text-dark">{user.name}</span>
                            </td>
                            <td>
                              <span className="text-muted">{user.email || "—"}</span>
                            </td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: roleConf.bg,
                                  color: roleConf.color,
                                  border: `1px solid ${roleConf.border}`,
                                  fontSize: "11px",
                                  padding: "4px 8px",
                                }}
                              >
                                {roleConf.label}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  user.status === "ACTIVE" ? "bg-success" : "bg-secondary"
                                }`}
                                style={{ fontSize: "10px" }}
                              >
                                {user.status || "ACTIVE"}
                              </span>
                            </td>
                            <td>
                              <span className="text-muted tx-12">
                                {user.createdAt ? formateDateWithMonth(user.createdAt) : "—"}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Table Pagination */}
              {totalFilteredUsers > userLimit && (
                <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-2 pb-2">
                  <span className="text-muted tx-12 font-weight-500 my-1">
                    Showing <span className="text-dark font-weight-semibold">{(userPage - 1) * userLimit + 1}</span> to{" "}
                    <span className="text-dark font-weight-semibold">{Math.min(userPage * userLimit, totalFilteredUsers)}</span> of{" "}
                    <span className="text-dark font-weight-semibold">{totalFilteredUsers}</span> entries
                  </span>
                  <div className="my-1">
                    <CustomPagination
                      modalPagination={true}
                      currentPage={userPage}
                      datalength={totalFilteredUsers}
                      itemperpage={userLimit}
                      setPage={(p) => setUserPage(p)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: BROADCAST HISTORY */}
          {activeTab === "history" && (
            <div>
              {/* History Search */}
              <div className="row mb-3 justify-content-between align-items-center g-2">
                <div className="col-md-6 col-sm-12">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="fe fe-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search history by subject or sender..."
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      style={{ fontSize: "13px" }}
                    />
                    {historySearch && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setHistorySearch("")}
                      >
                        <i className="fe fe-x"></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="col-auto">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={fetchHistory}
                  >
                    <i className="fe fe-refresh-cw me-1"></i> Refresh Logs
                  </button>
                </div>
              </div>

              {/* History Table */}
              <div className="table-responsive border rounded">
                <table className="table table-hover table-striped mb-0 text-nowrap" style={{ fontSize: "13px" }}>
                  <thead className="bg-light">
                    <tr>
                      <th>SR. NO.</th>
                      <th>DATE & TIME</th>
                      <th>SUBJECT</th>
                      <th>TARGET AUDIENCE</th>
                      <th>RECIPIENTS</th>
                      <th>SENT BY</th>
                      <th>STATUS</th>
                      <th className="text-center">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyLoading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-5">
                          <Loader size="sm" content="Loading broadcast history..." vertical />
                        </td>
                      </tr>
                    ) : historyList.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-5 text-muted">
                          <i className="fe fe-inbox mb-2" style={{ fontSize: "2rem", display: "block" }}></i>
                          No broadcasts sent yet.
                        </td>
                      </tr>
                    ) : (
                      historyList.map((item, idx) => {
                        const serial = (historyPage - 1) * historyLimit + (idx + 1);

                        let targetLabel = "All Users";
                        if (item.targetType === "ROLES") {
                          targetLabel = `Roles: ${(item.targetRoles || []).join(", ")}`;
                        } else if (item.targetType === "SELECTED") {
                          targetLabel = `Selected Users (${item.recipientCount})`;
                        }

                        return (
                          <tr key={item.id}>
                            <td>{serial}</td>
                            <td>
                              <span className="font-weight-semibold text-dark">
                                {item.createdAt ? formateDateWithMonth(item.createdAt) : "—"}
                              </span>
                              <br />
                              <small className="text-muted">
                                {item.createdAt ? new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}
                              </small>
                            </td>
                            <td>
                              <span
                                className="font-weight-bold text-dark"
                                style={{ maxWidth: "260px", display: "inline-block", overflow: "hidden", textOverflow: "ellipsis" }}
                                title={item.subject}
                              >
                                {item.subject}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark border" style={{ fontSize: "11px" }}>
                                {targetLabel}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-primary text-white" style={{ fontSize: "11px" }}>
                                {item.recipientCount || 0} user(s)
                              </span>
                            </td>
                            <td>
                              <span className="text-dark tx-12">{item.senderName || "Admin"}</span>
                            </td>
                            <td>
                              <span className="badge bg-success" style={{ fontSize: "10px" }}>
                                {item.status || "SENT"}
                              </span>
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-xs"
                                onClick={() => {
                                  setSelectedBroadcast(item);
                                  setDetailModalOpen(true);
                                }}
                              >
                                <i className="fe fe-eye me-1"></i> View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* History Pagination */}
              {historyTotal > historyLimit && (
                <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-2 pb-2">
                  <span className="text-muted tx-12 font-weight-500 my-1">
                    Showing <span className="text-dark font-weight-semibold">{(historyPage - 1) * historyLimit + 1}</span> to{" "}
                    <span className="text-dark font-weight-semibold">{Math.min(historyPage * historyLimit, historyTotal)}</span> of{" "}
                    <span className="text-dark font-weight-semibold">{historyTotal}</span> entries
                  </span>
                  <div className="my-1">
                    <CustomPagination
                      modalPagination={true}
                      currentPage={historyPage}
                      datalength={historyTotal}
                      itemperpage={historyLimit}
                      setPage={(p) => setHistoryPage(p)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* DETAILS MODAL */}
      <Modal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        size="md"
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title style={{ fontWeight: 700, color: "#1e293b", fontSize: "16px" }}>
            Broadcast Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px" }}>
          {selectedBroadcast && (
            <div>
              <div className="mb-3 p-3 bg-light rounded border">
                <div className="row g-2" style={{ fontSize: "13px" }}>
                  <div className="col-sm-6">
                    <span className="text-muted d-block tx-11">DATE SENT</span>
                    <strong>{selectedBroadcast.createdAt ? formateDateWithMonth(selectedBroadcast.createdAt) : "—"}</strong>
                  </div>
                  <div className="col-sm-6">
                    <span className="text-muted d-block tx-11">SENT BY</span>
                    <strong>{selectedBroadcast.senderName || "Admin"}</strong> ({selectedBroadcast.senderEmail || "—"})
                  </div>
                  <div className="col-sm-6 mt-2">
                    <span className="text-muted d-block tx-11">TARGET TYPE</span>
                    <strong>{selectedBroadcast.targetType}</strong>
                  </div>
                  <div className="col-sm-6 mt-2">
                    <span className="text-muted d-block tx-11">TOTAL RECIPIENTS</span>
                    <strong className="text-primary">{selectedBroadcast.recipientCount} recipient(s)</strong>
                  </div>
                </div>
              </div>

              <div className="form-group mb-2">
                <label className="font-weight-bold tx-12 text-muted">SUBJECT</label>
                <div className="p-2 border rounded bg-white text-dark font-weight-bold" style={{ fontSize: "14px" }}>
                  {selectedBroadcast.subject}
                </div>
              </div>

              <div className="form-group mb-2">
                <label className="font-weight-bold tx-12 text-muted">MESSAGE BODY</label>
                <div
                  className="p-3 border rounded bg-white"
                  style={{
                    maxHeight: "260px",
                    overflowY: "auto",
                    fontSize: "13px",
                    lineHeight: "1.6",
                    whiteSpace: "pre-line",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: selectedBroadcast.message
                      ? selectedBroadcast.message.replace(/\n/g, "<br/>")
                      : "",
                  }}
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setDetailModalOpen(false)} appearance="primary">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BroadcastPage;
